import React, {useEffect, useState} from "react";
import classNames from "classnames";
import numeral from "numeral";

import { Button, Image, useDisclosure, addToast } from "@heroui/react";
import { isUserLoggedIn } from 'src/shared/utils/session'

import LoginModal from "src/shared/components/LoginModal";

import { 
  useCreateCartMutation,
  useClearCartMutation,
  useAddToCartMutation,
} from "src/services/rtk_api/order/CartApi";

import { useAdvanceCheckoutMutation } from "src/services/rtk_api/order/CheckoutApi";

import { useGetProductQuery } from "../../services/rtk_api/product/ProductApi";

import IconGreenCheck from 'images/Icon-Check.svg';


const getNumberCredits = (variant) => {
  const {options_text} = variant;
  if(!options_text) return 0;
  const number = options_text.match(/\d+/);
  return number ? parseInt(number[0], 10) : null;
}

const SelectedVariant = ({preparingOrder, selectedVariant, onPressBuyNow}) => {
  const { price, display_price, sku } = selectedVariant;

  //Divide price in two parts: before and after the decimal point
  const priceParts = display_price.split('.');
  const priceBeforeDecimal = priceParts[0];
  const priceAfterDecimal = priceParts[1] ? `${priceParts[1] }` : '';

  const numberCredits = getNumberCredits(selectedVariant);
  const unit_price = numberCredits ? (price / numberCredits).toFixed(2) : 0;

  return (
    <div className="selected-variant ">
      <div className="title flex justify-center items-start">
        <h5 className="price-before-decimal">{priceBeforeDecimal}</h5>
        <h6 className="price-after-decimal">{priceAfterDecimal}</h6>
      </div>
      <div className="subtitle flex justify-center items-start mt-[14px]">
        <span className="credits">{numberCredits} credits for {priceBeforeDecimal} ({numeral(unit_price).format('$0,0.00')})</span>
      </div>
      <div className="mt-[29px]">
        <Button
          isLoading={preparingOrder}
          className="bg-[#007CA1] font-extrabold text-white text-[14px] w-full"
          onPress={onPressBuyNow}
        >
          {preparingOrder ? "Processing..." : "Buy Now"}
        </Button>
      </div>
    </div>
  )

}

const PricingPage = () => {

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [orderToken, setOrderToken] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [preparingOrder, setPreparingOrder] = useState(false);
  const { data: product, isLoading, isError, isSuccess } = useGetProductQuery({ product_slug: 'credit-pack' });
  const { isOpen: isModalOpen, onOpen: onModalOpen, onOpenChange: onModalOpenChange } = useDisclosure();

  const [createCart, {
    data: createdCartData,
    isLoading: isCreatingCart,
    isSuccess: isCartCreated,
    isError: isCartCreateError,
    error: cartCreateError,
  }] = useCreateCartMutation();

  const [clearCart, {
    data: clearedCartData,
    isLoading: isClearingCart,
    isSuccess: isCartCleared,
    isError: isCartClearError,
    error: cartClearError,
  }] = useClearCartMutation();

  const [addToCart, {
    data: addedToCartData,
    isLoading: isAddingToCart,
    isSuccess: isCartAdded,
    isError: isCartAddError,
    error: cartAddError,
  }] = useAddToCartMutation();

  const [advanceCheckout, {
    data: advancedCheckoutData,
    isLoading: isAdvancingCheckout,
    isSuccess: isCheckoutAdvanced,
    isError: isCheckoutAdvanceError,
    error: checkoutAdvanceError,
  }] = useAdvanceCheckoutMutation();

  const onClickVariant = (variant) => {
    if(preparingOrder) return;

    if(!variant) {
      addToast({
        title: "Variant not found",
        severity: "danger",
        color: "danger"
      });
      return;
    }

    setSelectedVariant(variant);
  }

  const onPressBuyNow = () => {
    if (!selectedVariant) {
      addToast({
        title: "Please select a variant first",
        severity: "danger",
        color: "danger"
      });
      return;
    }

    //Validate if user is logged in
    if (!isUserLoggedIn()) {
      onModalOpen()
      return;
    }

    // Create a new cart
    setPreparingOrder(true);
    createCart();
  }

  useEffect(() => {
    setLoggedIn(isUserLoggedIn());
  }, []);

  useEffect(() => {
    if (isSuccess && product) {
      // Assuming the first variant is the default selection
      const defaultVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
      setSelectedVariant(defaultVariant);
      return;
    }

  }, [product, isSuccess]);

  useEffect(() => {
    if(isCartCreated) {
      const new_order_token = createdCartData?.data?.attributes?.token;
      setOrderToken(new_order_token);
      clearCart({ order_token: new_order_token });
      return;
    }

    if(isCartCreateError && cartCreateError) {
      addToast({
        title: cartCreateError?.message || "Failed to create cart",
        severity: "danger",
        color: "danger"
      });
      setPreparingOrder(false);
      return;
    }
  }, [isCartCreated, isCartCreateError]);

  useEffect(() => {
    if(isCartCleared) {
      addToCart({
        variantId: selectedVariant.id,
        quantity: 1,
        order_token: orderToken,
      })
      return;
    }

    if(isCartClearError && cartClearError) {
      addToast({
        title: cartClearError?.message || "Failed to clear cart",
        severity: "danger",
        color: "danger"
      });
      setPreparingOrder(false);
      return;
    }
  }, [isCartCleared, isCartClearError]);

  useEffect(() => {
    if(isCartAdded) {
      advanceCheckout({ order_token: orderToken });
    }

    if(isCartAddError && cartAddError) {
      addToast({
        title: cartAddError?.message || "Failed to add to cart",
        severity: "danger",
        color: "danger"
      });
      setPreparingOrder(false);
      return;
    }
  }, [isCartAdded, isCartAddError]);

  useEffect(() => {
    if(isCheckoutAdvanced) {
      window.location.href = '/checkout';
      return;
    }

    if(isCheckoutAdvanceError && checkoutAdvanceError) {
      addToast({
        title: checkoutAdvanceError?.message || "Failed to advance checkout",
        severity: "danger",
        color: "danger"
      });
      setPreparingOrder(false);
      return;
    }

  }, [isCheckoutAdvanced, isCheckoutAdvanceError]);

  return (
    <div className="h-full" id="pricing">
      <div className="container">
        <div className="flex justify-center items-center">
          <h4 className="title">Choose a plan that works for you</h4>
        </div>
        { !loggedIn && <div className="flex justify-center items-center mt-[27px]">
            <h5 className="subtitle">50 Free Credit to Sign Up!</h5>
          </div>
        }
        <div className="plan-container grid grid-cols-1 md:grid-cols-2 md:gap-x-[25px] mt-8">
          <div>
            { isLoading && <p>Loading...</p> }
            { isError && <p>Error loading product</p> }
            { !isLoading && !isError && product && product.variants && (
              <div className="credit-packs grid grid-cols-1 gap-y-4">
                {product.variants.map((variant, index) => {
                  const { price, display_price, sku } = variant;

                  const priceParts = display_price.split('.');
                  const priceBeforeDecimal = priceParts[0];
                  const numberCredits = getNumberCredits(variant);

                  return (
                    <div key={`${sku}`}
                      className={classNames("credit-pack", {
                        "selected": selectedVariant && selectedVariant.sku === sku,
                        "cursor-pointer": !preparingOrder,
                      })}
                      onClick={() => onClickVariant(variant)}  
                    >
                        <div className="flex justify-between items-center">
                          <div />
                          <div className="flex justify-center items-center">
                            <span className="credits me-[10px]">{numberCredits}</span>
                            <span className="credits-for me-[17px]">credits for</span>
                            <span className="price">{priceBeforeDecimal}</span>
                          </div>
                          <Image
                            src={IconGreenCheck}
                            alt="icon-check"
                            className={selectedVariant && selectedVariant.sku === sku ? 'visible' : 'invisible'}
                          />
                        </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
          <div className="flex justify-center items-center buy-product-block">
            { isLoading && <p>Loading...</p> }
            { isError && <p>Error loading product</p> }
            { !isLoading && !isError && selectedVariant && (
              <SelectedVariant 
                preparingOrder={preparingOrder}
                selectedVariant={selectedVariant} 
                onPressBuyNow={onPressBuyNow}
              />
            )}
          </div>
        </div>
      </div>
      <LoginModal
        mode={"LOGIN"}
        isOpen={isModalOpen}
        onOpenChange={onModalOpenChange}
        onOk={() => {
          onModalOpenChange(false);
          createCart();
        }}
      />
    </div>
  )
}

export default PricingPage;